import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Pencil, Plus, Printer, StickyNote, Pill, TestTube, Package } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill, KpiCard } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PatientPicker } from "@/components/PatientPicker";
import {
  CaseSheetDoc,
  DischargeSummaryDoc,
  HospitalInvoiceDoc,
  PrescriptionDoc,
  PrintPreview,
  useActiveTemplate,
} from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmyTime, num, titleize } from "@/lib/format";

const EMPTY_RX_LINE = { name: "", strength: "", dosage: "", frequency: "", duration: "", instructions: "" };

/**
 * Bill preview for a draft IPD admission — same visual pattern as the
 * Maternity "Package & Bill" modal: package summary, extras list, bill lines
 * and totals. Pure presentational component: it never saves anything and
 * only renders the lines/totals computed from the live admission form.
 */
function AdmitBillPreview({ lines, totals, patient, doctor, ward, bed, pkg, packageExtraIds, formError, admitSaving, onCancel, onSave }) {
  const selectedExtras = (pkg?.extras || []).filter((ex) => ex.active !== false && (packageExtraIds || []).includes(ex.service_id));
  if (!lines || !totals) {
    return (
      <div className="space-y-4 text-sm" data-testid="admit-bill-preview">
        <p className="text-sm text-slate-500">No billable charges yet. Select a ward/bed, package, or add an initial charge to build the bill.</p>
        <div className="flex gap-3 justify-end">
          <button type="button" className={ghostBtnCls} onClick={onCancel} data-testid="admit-preview-cancel">Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4 text-sm" data-testid="admit-bill-preview">
      <div className="grid grid-cols-2 gap-3">
        <div><p className="text-xs text-slate-500">Patient</p><p className="font-medium">{patient?.name || "—"}</p></div>
        <div><p className="text-xs text-slate-500">UHID</p><p className="font-medium">{patient?.uhid || "—"}</p></div>
        <div><p className="text-xs text-slate-500">Doctor</p><p className="font-medium">Dr. {doctor?.name || "—"}</p></div>
        <div><p className="text-xs text-slate-500">Ward / bed</p><p className="font-medium">{ward?.name || "—"} · {bed?.bed_number || "—"}</p></div>
      </div>
      {pkg && (
        <div className="rounded-md bg-slate-50 p-4" data-testid="admit-preview-package">
          <div className="flex items-baseline justify-between">
            <p className="font-semibold text-slate-900">{pkg.name}</p>
            <p className="font-semibold text-slate-900" data-testid="admit-preview-package-price">{inr(pkg.price)}</p>
          </div>
          {(pkg.inclusions || []).length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {(pkg.inclusions || []).map((i, idx) => (
                <li key={idx} className="rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-xs text-slate-600">{i}</li>
              ))}
            </ul>
          )}
          {selectedExtras.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-100" data-testid="admit-preview-extras">
              {selectedExtras.map((ex) => (
                <li key={ex.service_id} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-700">{ex.name} <span className="text-slate-400">× {Number(ex.quantity_default || 1)}</span></span>
                  <span className="text-slate-800">{inr(Number(ex.charge || 0))}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="rounded-lg border border-slate-200 p-4" data-testid="admit-preview-bill">
        <p className="overline text-[#EA580C] mb-3">Bill</p>
        {lines.length === 0 ? (
          <p className="text-sm text-slate-400">No billable lines yet — bed charges and selected package/charges will appear here.</p>
        ) : (
          <>
            <table className="w-full"><tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{l.description}<span className="text-xs text-slate-400"> · {l.quantity} × {inr(l.rate)}</span></td><td className="py-1.5 text-right">{inr(Math.max(l.quantity * l.rate - (l.discount || 0), 0))}</td></tr>
              ))}
            </tbody></table>
            <div className="space-y-1 border-t border-slate-200 pt-2">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{inr(totals.subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>GST / tax</span><span>{inr(totals.tax)}</span></div>
              {totals.discountAmount > 0 && <div className="flex justify-between text-slate-600"><span>Discount</span><span>-{inr(totals.discountAmount)}</span></div>}
              <div className="flex justify-between font-semibold text-base pt-1"><span>Grand total</span><span data-testid="admit-preview-total">{inr(totals.total)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Advance / paid</span><span>{inr(totals.paid)}</span></div>
              <div className="flex justify-between text-amber-700"><span>Due</span><span>{inr(totals.due)}</span></div>
            </div>
          </>
        )}
      </div>
      {formError && <p className="text-sm text-red-600" data-testid="admit-preview-error">{formError}</p>}
      <div className="flex gap-3 justify-end">
        <button type="button" className={ghostBtnCls} onClick={onCancel} data-testid="admit-preview-cancel">Cancel</button>
        <button type="button" className={primaryBtnCls} onClick={onSave} disabled={admitSaving} data-testid="admit-preview-save-print">{admitSaving ? "Saving…" : "Save & Print"}</button>
      </div>
    </div>
  );
}

export default function Ipd() {
  const { can, user } = useAuth();
  const rxTpl = useActiveTemplate("hospital", "ipd_document");
  const caseTpl = useActiveTemplate("hospital", "case_sheet");
  const dischargeTpl = useActiveTemplate("hospital", "discharge_summary");
  const [wards, setWards] = useState(null);
  const [admissions, setAdmissions] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState("");
  const [wardModalOpen, setWardModalOpen] = useState(false);
  const [bedModalTab, setBedModalTab] = useState("ward");
  const [editWardId, setEditWardId] = useState(null);
  const [editBedId, setEditBedId] = useState(null);
  const [editWardForm, setEditWardForm] = useState({ name: "", category: "general", bed_count: "", bed_prefix: "", daily_charge: "", facilities: "", active: true });
  const [editBedForm, setEditBedForm] = useState({ ward_id: "", bed_number: "" });
  const [wardModalBusy, setWardModalBusy] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [ward, setWard] = useState({ name: "", category: "general", bed_count: "10", bed_prefix: "", daily_charge: "", facilities: "", active: true });
  const [bed, setBed] = useState({ ward_id: "", bed_number: "" });
  const [admit, setAdmit] = useState({ patient_id: "", doctor_id: "", bed_id: "", diagnosis: "", treatment_plan: "", vitals: "", notes: "", advance_payment: "", discount: "", discount_type: "amount", tax_percent: "", include_direct_consultation: false, package_id: "", package_extra_ids: [] });
  const [admitCharges, setAdmitCharges] = useState([]);
  const [moduleGst, setModuleGst] = useState(0);
  const [admitPreview, setAdmitPreview] = useState(false);
  const [admitReceipt, setAdmitReceipt] = useState(null);
  const [admitSaving, setAdmitSaving] = useState(false);
  const [admitWardId, setAdmitWardId] = useState("");
  const [picked, setPicked] = useState(null);
  const [formError, setFormError] = useState("");
  const [service, setService] = useState(null);
  const [sForm, setSForm] = useState({ description: "", amount: "" });
  const [noteFor, setNoteFor] = useState(null);
  const [noteForm, setNoteForm] = useState({ note_type: "progress", vitals: "", note: "" });
  const [discharge, setDischarge] = useState(null);
  const [dForm, setDForm] = useState({ discharge_diagnosis: "", hospital_course: "", procedures: "",
    condition_at_discharge: "", discharge_medicines: "", summary: "", follow_up_date: "", follow_up_instructions: "" });
  const [caseData, setCaseData] = useState(null);
  const [previewKind, setPreviewKind] = useState("");
  const [rxFor, setRxFor] = useState(null);
  const [rxForm, setRxForm] = useState({ doctor_id: "", diagnosis: "", advice: "", follow_up_date: "", medicines: [{ ...EMPTY_RX_LINE }] });
  const [rxPreview, setRxPreview] = useState(null);
  const [labFor, setLabFor] = useState(null);
  const [labTests, setLabTests] = useState([]);
  const [labSel, setLabSel] = useState([]);
  const [clearance, setClearance] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [invoiceCharges, setInvoiceCharges] = useState([]);
  const [invoiceServiceForm, setInvoiceServiceForm] = useState({ description: "", amount: "" });
  const [invoiceReverseTarget, setInvoiceReverseTarget] = useState(null);
  const [invoiceReverseReason, setInvoiceReverseReason] = useState("");
  const [invoiceReceipt, setInvoiceReceipt] = useState(null);
  const [showRefund, setShowRefund] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: "", mode: "cash", reference_no: "", reason: "" });
  const [invoicePayment, setInvoicePayment] = useState({
    amount: "",
    mode: "cash",
    reference_no: "",
  });
  const [staff, setStaff] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageExtras, setPackageExtras] = useState([]);
  const [chargesFor, setChargesFor] = useState(null);
  const [reverseChargeTarget, setReverseChargeTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [nurseFor, setNurseFor] = useState(null);
  const [nurseHistory, setNurseHistory] = useState([]);
  const [nurseForm, setNurseForm] = useState({ staff_user_id: "", role_label: "nurse", reason: "" });
  const [cleanupFor, setCleanupFor] = useState(null);
  const [cleanupReason, setCleanupReason] = useState("");
  const [cleanupConfirmed, setCleanupConfirmed] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [splitMode, setSplitMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState([{ amount: "", mode: "cash", reference_no: "" }]);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [bedCorrection, setBedCorrection] = useState(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionStatus, setCorrectionStatus] = useState("available");
  const [correctionBusy, setCorrectionBusy] = useState(false);
  const [bedDetails, setBedDetails] = useState(null);
  const [bedDetailsBusy, setBedDetailsBusy] = useState(false);
  const [reserveFor, setReserveFor] = useState(null);
  const [reserveForm, setReserveForm] = useState({ patient_id: "", expected_admission_date: "", notes: "" });
  const [reserveBusy, setReserveBusy] = useState(false);
  const [reservePatient, setReservePatient] = useState(null);
  const [editClinical, setEditClinical] = useState(null);
  const [editClinicalForm, setEditClinicalForm] = useState({ diagnosis: "", treatment_plan: "", vitals: "", notes: "" });
  const [editClinicalBusy, setEditClinicalBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, a] = await Promise.all([api.get("/hospital/wards"), api.get("/hospital/admissions")]);
      setWards(w.data.items); setAdmissions(a.data.items);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => {
    if (!can("hospital.ipd.manage")) return;
    load();
    api.get("/hospital/doctors").then(({ data }) => setDoctors(data.items)).catch(() => {});
    api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
    api.get("/hospital/packages", { params: { active: true } }).then(({ data }) => setPackages(data.items || [])).catch(() => {});
    if (can("lab.orders.create")) {
      api.get("/lab/tests", { params: { active: true } }).then(({ data }) => setLabTests(data.items || [])).catch(() => {});
    }
    api.get("/hospital/settings/ipd").then(({ data }) => setClearance(data.require_payment_clearance)).catch(() => {});
    api.get("/settings/full").then(({ data }) => setModuleGst(Number(data.business?.module_gst?.hospital?.percent || 0))).catch(() => {});
  }, [load, can]);

  if (!can("hospital.ipd.manage")) return <NoAccess what="IPD and bed management" />;
  if (error) return <ErrorState message={error} />;
  if (!wards) return <Loading />;

  const canCleanup = can("hospital.records.cleanup")
    && ["business_admin", "hospital_admin"].includes(user?.role);

  const beds = wards.flatMap((w) => (w.beds || []).map((b) => ({ ...b, ward: w.name,
    ward_id: w.ward_id, ward_category: w.category || w.ward_type || "general",
    is_room: w.is_room || b.is_room })));
  const freeBeds = beds.filter((b) => b.status === "available");
  const admitWard = wards.find((w) => w.ward_id === admitWardId);
  const wardFreeBeds = admitWardId ? freeBeds.filter((b) => b.ward_id === admitWardId) : [];
  const unitWord = (w) => (w?.is_room ? "Room" : "Bed");

  const chooseAdmitWard = (wid) => {
    setAdmitWardId(wid);
    const first = freeBeds.find((b) => b.ward_id === wid);
    setAdmit((a) => ({ ...a, bed_id: first?.bed_id || "" }));
  };

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const ipdLines = () => {
    const lines = [];
    const dr = (doctors || []).find((d) => d.doctor_id === admit.doctor_id);
    if (admit.include_direct_consultation) lines.push({ description: `Direct IPD consultation — Dr. ${dr?.name || ""}`, quantity: 1, rate: Number(dr?.consultation_fee || 0), discount: 0, tax_percent: null });
    lines.push({ description: "Bed charges (1 day)", quantity: 1, rate: Number(admitWard?.daily_charge || 0), discount: 0, tax_percent: null });
    // Special Package line (if selected)
    if (admit.package_id) {
      const pkg = packages.find((p) => p.package_id === admit.package_id);
      if (pkg) {
        lines.push({ description: pkg.name, quantity: 1, rate: Number(pkg.price || 0), discount: 0, tax_percent: null, kind: "package" });
        (pkg.extras || []).filter((ex) => ex.active && (admit.package_extra_ids || []).includes(ex.service_id)).forEach((ex) => {
          lines.push({ description: ex.name, quantity: Number(ex.quantity_default || 1), rate: Number(ex.charge || 0), discount: 0, tax_percent: Number(ex.tax_percent || 0), kind: "package_extra" });
        });
      }
    }
    admitCharges.filter((c) => c.description.trim()).forEach((c) => lines.push({ description: c.description, quantity: Number(c.quantity || 1), rate: Number(c.rate || 0), discount: Number(c.discount || 0), tax_percent: c.tax_percent === "" ? null : Number(c.tax_percent) }));
    return lines;
  };
  const calcBill = (lines) => {
    let subtotal = 0, lineTax = 0;
    lines.forEach((l) => { const taxable = Math.max(l.quantity * l.rate - (l.discount || 0), 0); subtotal += taxable; const t = (l.tax_percent === null || l.tax_percent === undefined) ? moduleGst : l.tax_percent; lineTax += taxable * t / 100; });
    subtotal = round2(subtotal);
    const billTaxPercent = admit.tax_percent === "" ? 0 : Number(admit.tax_percent);
    const tax = round2(lineTax + subtotal * billTaxPercent / 100);
    const invoiceValue = round2(subtotal + tax);
    const disc = Number(admit.discount || 0);
    const discountAmount = admit.discount_type === "percent" ? round2(invoiceValue * disc / 100) : round2(disc);
    const total = round2(Math.max(invoiceValue - discountAmount, 0));
    const paid = Math.min(round2(Number(admit.advance_payment || 0)), total);
    return { subtotal, tax, discountAmount, total, paid, due: round2(Math.max(total - paid, 0)) };
  };

  const admitPreviewLines = admitPreview ? ipdLines() : [];
  const admitPreviewTotals = admitPreview ? calcBill(admitPreviewLines) : null;

  const openBedCorrection = (b) => {
    setBedCorrection(b);
    setCorrectionReason("");
    setCorrectionStatus(b.status === "available" ? "occupied" : "available");
  };

  const submitBedCorrection = async (event) => {
    event.preventDefault();
    if (!bedCorrection?.bed_id) return;
    setCorrectionBusy(true);
    try {
      await api.post(`/hospital/beds/${bedCorrection.bed_id}/correct-status`, { status: correctionStatus, reason: correctionReason.trim() || "Bed status correction" });
      toast.success("Bed status corrected.");
      setBedCorrection(null); setCorrectionReason(""); setCorrectionStatus("available"); load();
    } catch (e) { toast.error(apiError(e)); }
    setCorrectionBusy(false);
  };

  const openBedDetails = async (b) => {
    setBedDetails({ bed: b, ward: wards.find((w) => w.ward_id === b.ward_id), occupant: null, warnings: [] });
    setBedDetailsBusy(true);
    try {
      const { data } = await api.get(`/hospital/beds/${b.bed_id}/details`);
      setBedDetails(data);
    } catch (e) { toast.error(apiError(e)); }
    setBedDetailsBusy(false);
  };

  const openReserve = (b) => {
    setReserveFor(b);
    setReservePatient(null);
    setReserveForm({ patient_id: "", expected_admission_date: "", notes: "" });
  };

  const submitReserve = async (event) => {
    event.preventDefault();
    if (!reserveFor?.bed_id || !reservePatient?.patient_id) return;
    setReserveBusy(true);
    try {
      await api.post(`/hospital/beds/${reserveFor.bed_id}/reserve`, {
        patient_id: reservePatient.patient_id,
        expected_admission_date: reserveForm.expected_admission_date,
        notes: reserveForm.notes,
      });
      toast.success(`Bed ${reserveFor.bed_number} reserved for ${reservePatient.name}.`);
      setReserveFor(null); setReservePatient(null); load();
    } catch (e) { toast.error(apiError(e)); }
    setReserveBusy(false);
  };

  const submitCancelReservation = async (b) => {
    try {
      await api.post(`/hospital/beds/${b.bed_id}/cancel-reservation`);
      toast.success(`Reservation for bed ${b.bed_number} cancelled.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAdmitPreview = (event) => {
    event.preventDefault();
    setFormError("");
    const discount = Number(admit.discount || 0);
    if (admit.discount_type === "percent" && discount > 100) { setFormError("Percentage discount cannot exceed 100%."); return; }
    if (!admit.bed_id) { setFormError("Select an available bed."); return; }
    setAdmitPreview(true);
  };

  const confirmAdmit = async () => {
    if (admitSaving) return;
    setAdmitSaving(true);
    setFormError("");
    try {
      const charges = admitCharges.filter((c) => c.description.trim()).map((c) => ({
        description: c.description, quantity: Number(c.quantity || 1), rate: Number(c.rate || 0),
        discount: Number(c.discount || 0), tax_percent: c.tax_percent === "" ? null : Number(c.tax_percent) }));
      const { data: admRes } = await api.post("/hospital/admissions", {
        ...admit,
        advance_payment: Number(admit.advance_payment || 0),
        discount: Number(admit.discount || 0),
        tax_percent: admit.tax_percent === "" ? null : Number(admit.tax_percent),
        initial_charges: charges,
      });
      let receiptData = null;
      try {
        const { data: gi } = await api.post(`/hospital/admissions/${admRes.admission.admission_id}/generate-invoice`);
        const { data: inv } = await api.get(`/hospital/invoices/${gi.invoice.invoice_id}`);
        receiptData = { invoice: inv.invoice, patient: inv.patient, business: inv.business };
      } catch { /* invoice generated later if no billable charge */ }
      toast.success("IPD admission successful.");
      setAdmitPreview(false); setAdmitOpen(false); setPicked(null); setAdmitWardId("");
      setAdmit({ patient_id: "", doctor_id: "", bed_id: "", diagnosis: "", treatment_plan: "", vitals: "", notes: "", advance_payment: "", discount: "", discount_type: "amount", tax_percent: "", include_direct_consultation: false, package_id: "", package_extra_ids: [] });
      setAdmitCharges([]);
      if (receiptData) setAdmitReceipt(receiptData);
      load();
    } catch (e) { setFormError(apiError(e)); toast.error(apiError(e)); }
    finally { setAdmitSaving(false); }
  };

  const save = async (fn, done) => {
    setFormError("");
    try { await fn(); done(); load(); } catch (e) { setFormError(apiError(e)); toast.error(apiError(e)); }
  };

  const openCase = async (a, kind) => {
    try {
      const { data } = await api.get(`/hospital/admissions/${a.admission_id}/case`);
      setCaseData(data); setPreviewKind(kind);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitLabs = async (e) => {
    e.preventDefault();
    if (!labSel.length) { toast.error("Select at least one test."); return; }
    try {
      const { data } = await api.post(`/hospital/admissions/${labFor.admission_id}/lab-order`, { test_ids: labSel });
      toast.success(`Lab order ${data.lab_order.order_no} created (pending billing).`);
      setLabFor(null); setLabSel([]);
    } catch (err) { toast.error(apiError(err)); }
  };

  const openCharges = async (a) => {
    try {
      const { data } = await api.get(`/hospital/admissions/${a.admission_id}/case`);
      setChargesFor({ admission: a, services: (data.admission || data).services || [] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitReverseCharge = async (event) => {
    event.preventDefault();
    if (reverseReason.trim().length < 3) { toast.error("A reversal reason is required."); return; }
    try {
      await api.post(`/hospital/admissions/${chargesFor.admission.admission_id}/services/${reverseChargeTarget.service_id}/reverse`,
        { reason: reverseReason.trim() });
      toast.success("Charge reversed. Original entry preserved in history.");
      setReverseChargeTarget(null); setReverseReason("");
      openCharges(chargesFor.admission); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openNurse = async (a) => {
    try {
      const { data } = await api.get(`/hospital/admissions/${a.admission_id}/staff-assignments`);
      setNurseFor(a); setNurseHistory(data.items || []);
      setNurseForm({ staff_user_id: "", role_label: "nurse", reason: "" });
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitNurse = async (event) => {
    event.preventDefault();
    if (!nurseForm.staff_user_id) { toast.error("Select a staff member to assign."); return; }
    try {
      await api.post(`/hospital/admissions/${nurseFor.admission_id}/staff-assignments`, nurseForm);
      toast.success("Care staff assigned. Previous assignment retained in history.");
      openNurse(nurseFor);
    } catch (err) { toast.error(apiError(err)); }
  };

  const applySettlement = (data) => {
    setInvoiceFor(data);
    setInvoiceCharges(data.services || []);
    setInvoicePayment({ amount: String(data.invoice?.outstanding || ""), mode: "cash", reference_no: "" });
  };

  const genInvoice = async (a) => {
    try {
      const { data } = await api.get(`/hospital/admissions/${a.admission_id}/settlement`);
      applySettlement(data);
      setInvoiceServiceForm({ description: "", amount: "" });
      setInvoiceReverseTarget(null);
      setInvoiceReverseReason("");
      setShowRefund(false);
      setRefundForm({ amount: String(data.settlement?.advance_refundable || ""), mode: "cash", reference_no: "", reason: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const reloadInvoiceView = async (admissionId) => {
    try {
      const { data } = await api.get(`/hospital/admissions/${admissionId}/settlement`);
      applySettlement(data);
      setRefundForm((f) => ({ ...f, amount: String(data.settlement?.advance_refundable || "") }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitRefund = async (event) => {
    event.preventDefault();
    const admissionId = invoiceFor?.admission?.admission_id;
    if (!admissionId) return;
    if (refundForm.reason.trim().length < 3) { toast.error("A refund reason is required."); return; }
    try {
      await api.post(`/hospital/admissions/${admissionId}/refund-advance`, {
        amount: Number(refundForm.amount || 0), mode: refundForm.mode,
        reference_no: refundForm.reference_no, reason: refundForm.reason.trim(),
      });
      toast.success("Advance refunded. Original deposit preserved in history.");
      setShowRefund(false);
      reloadInvoiceView(admissionId);
    } catch (err) { toast.error(apiError(err)); }
  };

  const addInvoiceCharge = async (event) => {
    event.preventDefault();
    const admissionId = invoiceFor?.admission?.admission_id;
    if (!admissionId) return;
    if (!invoiceServiceForm.description.trim() || !Number(invoiceServiceForm.amount)) {
      toast.error("Enter a description and a non-zero amount.");
      return;
    }
    try {
      await api.post(`/hospital/admissions/${admissionId}/services`, {
        description: invoiceServiceForm.description.trim(),
        amount: Number(invoiceServiceForm.amount || 0),
      });
      toast.success("Charge added to the invoice.");
      setInvoiceServiceForm({ description: "", amount: "" });
      reloadInvoiceView(admissionId);
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitInvoiceReverse = async (event) => {
    event.preventDefault();
    const admissionId = invoiceFor?.admission?.admission_id;
    if (!admissionId || !invoiceReverseTarget) return;
    if (invoiceReverseReason.trim().length < 3) {
      toast.error("A reversal reason is required."); return;
    }
    try {
      await api.post(
        `/hospital/admissions/${admissionId}/services/${invoiceReverseTarget.service_id}/reverse`,
        { reason: invoiceReverseReason.trim() },
      );
      toast.success("Charge reversed. Original entry preserved in history.");
      setInvoiceReverseTarget(null);
      setInvoiceReverseReason("");
      reloadInvoiceView(admissionId);
    } catch (err) { toast.error(apiError(err)); }
  };

  const collectSplitInvoicePayment = async (event) => {
    event.preventDefault();
    setPaymentSubmitting(true);
    try {
      const total = round2(splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
      if (total <= 0) { toast.error("Enter at least one payment amount."); setPaymentSubmitting(false); return; }
      if (total > Number(inv.outstanding || 0)) { toast.error("Split total exceeds outstanding."); setPaymentSubmitting(false); return; }
      await api.post(`/hospital/admissions/${invoiceFor.admission.admission_id}/payments`, {
        invoice_id: inv?.invoice_id, amount: total, mode: "split", reference_no: "",
        split_payments: splitPayments.map((p) => ({ amount: Number(p.amount), mode: p.mode, reference_no: p.reference_no })),
      });
      toast.success("Split payment recorded.");
      setInvoiceFor(null); setSplitMode(false); setSplitPayments([{ amount: "", mode: "cash", reference_no: "" }]); load();
    } catch (e) { toast.error(apiError(e)); }
    setPaymentSubmitting(false);
  };

  const collectInvoicePayment = async (event) => {
    event.preventDefault();
    const invoice = invoiceFor?.invoice;
    if (!invoice) {
      return;
    }
    try {
      await api.post(`/hospital/invoices/${invoice.invoice_id}/payments`, {
        amount: Number(invoicePayment.amount || 0),
        mode: invoicePayment.mode,
        reference_no: invoicePayment.reference_no,
      });
      toast.success("IPD payment recorded.");
      reloadInvoiceView(invoiceFor.admission.admission_id);
    } catch (requestError) {
      toast.error(apiError(requestError));
    }
  };

  const previewInvoiceBill = () => {
    if (!invoiceFor?.invoice) return;
    setInvoiceReceipt({
      invoice: invoiceFor.invoice,
      patient: invoiceFor.patient,
      business: invoiceFor.business,
    });
  };


  const submitCleanup = async (event) => {
    event.preventDefault();
    setCleanupError("");
    if (!cleanupConfirmed) {
      setCleanupError("Confirm permanent deletion to continue.");
      return;
    }
    try {
      await api.post(`/hospital/admissions/${cleanupFor.admission_id}/cleanup`, {
        confirmation: true,
        reason: cleanupReason.trim(),
      });
      toast.success("Empty IPD registration permanently removed and audited.");
      setCleanupFor(null);
      setCleanupReason("");
      setCleanupConfirmed(false);
      load();
    } catch (requestError) {
      setCleanupError(apiError(requestError));
    }
  };

  const saveRx = async (e) => {
    e.preventDefault();
    const medicines = rxForm.medicines.filter((m) => m.name.trim());
    if (!rxForm.doctor_id) { toast.error("Select the prescribing doctor."); return; }
    if (!medicines.length) { toast.error("Add at least one medicine."); return; }
    try {
      const { data } = await api.post("/hospital/prescriptions", {
        patient_id: rxFor.patient_id, doctor_id: rxForm.doctor_id, admission_id: rxFor.admission_id,
        diagnosis: rxForm.diagnosis, advice: rxForm.advice, follow_up_date: rxForm.follow_up_date,
        medicines });
      toast.success("Prescription created.");
      setRxFor(null);
      setRxForm({ doctor_id: "", diagnosis: "", advice: "", follow_up_date: "", medicines: [{ ...EMPTY_RX_LINE }] });
      setRxPreview({ rx: { ...data.prescription, encounter_type: "ipd" }, business: data.business });
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="ipd-page">
      <PageHeader overline="Hospital" title="IPD case management"
        subtitle="Admissions, clinical notes, prescriptions, case sheet and discharge summary — bed status stays in sync automatically."
        actions={
          <>
            {can("settings.manage") && (
              <label className="flex items-center gap-2 text-xs text-slate-600 mr-2" data-testid="ipd-clearance-toggle">
                <input type="checkbox" checked={clearance} onChange={async (e) => {
                  try { await api.put("/hospital/settings/ipd", { require_payment_clearance: e.target.checked }); setClearance(e.target.checked); toast.success("Discharge clearance policy updated."); }
                  catch (err) { toast.error(apiError(err)); }
                }} />
                Require payment before discharge
              </label>
            )}
            {can("hospital.beds.manage") && <button onClick={() => { setWardModalOpen(true); setBedModalTab("ward"); }} className={ghostBtnCls} data-testid="manage-beds-btn">Manage Wards & Beds</button>}
            <button onClick={() => setAdmitOpen(true)} className={primaryBtnCls} data-testid="admit-patient-btn">Admit patient</button>
          </>
        } />

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-8">
        <KpiCard label="Wards" value={num(wards.length)} onClick={() => {}} testid="kpi-wards" />
        <KpiCard label="Total beds" value={num(beds.length)} onClick={() => {}} testid="kpi-total-beds" />
        <KpiCard label="Available" value={num(freeBeds.length)} tone="success" onClick={() => {}} testid="kpi-free-beds" />
        <KpiCard label="Admitted" value={num(admissions.filter((a) => a.status === "admitted").length)} tone="accent" onClick={() => {}} testid="kpi-admitted" />
      </div>

      <div className="panel mb-8">
        <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">Bed map</h3></div>
        {beds.length === 0 ? <EmptyState title="No beds configured" subtitle="Add a ward with a bed/room count — beds are generated automatically." testid="beds-empty" /> : (
          <div className="divide-y divide-slate-100" data-testid="bed-map">
            {wards.filter((w) => (w.beds || []).length).map((w) => {
              const total = (w.beds || []).length;
              const occ = (w.beds || []).filter((b) => b.status === "occupied").length;
              return (
                <div key={w.ward_id} className="p-6" data-testid={`ward-block-${w.ward_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <p className="font-ui font-bold text-slate-900">{w.name}
                        <span className="ml-2 text-xs font-medium text-slate-400">{titleize(w.category || w.ward_type || "general")}{w.is_room ? " · Rooms" : ""}</span></p>
                    </div>
                    {can("hospital.beds.manage") && (
                      <button onClick={() => { setEditWardId(w.ward_id); setEditWardForm({ name: w.name, category: w.category || "general", bed_count: w.bed_count || "", bed_prefix: w.bed_prefix || "", daily_charge: w.daily_charge || "", facilities: w.facilities || "", active: w.active !== false }); setWardModalOpen(true); setBedModalTab("ward"); }} className="text-slate-400 hover:text-indigo-600" title="Edit ward" data-testid={`edit-ward-${w.ward_id}`}>
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                    <p className="text-xs text-slate-500" data-testid={`ward-counts-${w.ward_id}`}>
                      Total {w.is_room ? "rooms" : "beds"}: <strong className="text-slate-800">{total}</strong>
                      <span className="mx-2">·</span>Available: <strong className="text-emerald-600">{total - occ}</strong>
                      <span className="mx-2">·</span>Occupied: <strong className="text-red-600">{occ}</strong>
                    </p>
                    <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                    {(w.beds || []).map((b) => (
                      <div key={b.bed_id} className={`rounded-lg border p-4 ${b.status === "available" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                        <p className="font-ui font-bold text-slate-900">{b.bed_number}</p>
                        <p className="text-xs text-slate-500 mt-1">{w.is_room ? "Room" : "Bed"}</p>
                        <p className="text-[11px] mt-2 capitalize text-slate-600">{b.status}</p>
{can("hospital.beds.manage") && (
  <button onClick={() => openBedCorrection(b)} className="text-slate-400 hover:text-indigo-600" title="Correct bed status" data-testid={`correct-bed-${b.bed_id}`}>
    <Pencil className="h-3 w-3" />
  </button>
)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Table testid="admissions-table" columns={["Patient", "Ward / Bed", "Doctor", "Diagnosis", "Notes", "Admitted", "Status", "Actions"]}
        rows={admissions} empty={<EmptyState title="No admissions" subtitle="Admit a patient to occupy a bed." testid="admissions-empty" />}
        renderRow={(a) => (
          <tr key={a.admission_id} data-testid={`admission-row-${a.admission_id}`}>
            <Td className="font-medium text-slate-900">{a.patient_name}<span className="block text-xs text-slate-400">{a.patient_uhid}{a.admission_no ? ` · ${a.admission_no}` : ""}</span></Td>
            <Td className="text-slate-600">{a.ward_name} · {a.bed_number}</Td>
            <Td className="text-slate-600">Dr. {a.doctor_name}</Td>
            <Td className="text-slate-600 max-w-[180px] truncate">{a.diagnosis || "—"}</Td>
            <Td className="text-slate-600">{(a.case_notes || []).length}</Td>
            <Td className="text-slate-500 whitespace-nowrap">{dmyTime(a.admitted_at)}</Td>
            <Td><StatusPill status={a.status} /></Td>
            <Td>
              <div className="flex flex-wrap gap-2">
                {a.status === "admitted" && can("hospital.notes.manage") && (
                  <button onClick={() => { setNoteFor(a); setNoteForm({ note_type: "progress", vitals: "", note: "" }); }}
                    className={ghostBtnCls} data-testid={`add-note-${a.admission_id}`}><StickyNote className="h-3.5 w-3.5" /> Note</button>
                )}
                {a.status === "admitted" && can("hospital.prescriptions.manage") && (
                  <button onClick={() => { setRxFor(a); setRxForm({ doctor_id: a.doctor_id, diagnosis: a.diagnosis || "", advice: "", follow_up_date: "", medicines: [{ ...EMPTY_RX_LINE }] }); }}
                    className={ghostBtnCls} data-testid={`prescribe-${a.admission_id}`}><Pill className="h-3.5 w-3.5" /> Prescribe</button>
                )}
                <button onClick={() => openCase(a, "case")} className={ghostBtnCls} data-testid={`case-sheet-${a.admission_id}`}>
                  <FileText className="h-3.5 w-3.5" /> Case sheet
                </button>
                {a.status === "admitted" ? (
                  <>
                    <button onClick={() => openNurse(a)} className={ghostBtnCls} data-testid={`assign-nurse-${a.admission_id}`}>Nurse / staff</button>
                    {can("hospital.billing.manage") && <button onClick={() => genInvoice(a)} className={primaryBtnCls} data-testid={`gen-invoice-${a.admission_id}`}>Invoice &amp; payment</button>}
                    {can("lab.orders.create") && <button onClick={() => { setLabFor(a); setLabSel([]); }} className={ghostBtnCls} data-testid={`order-labs-${a.admission_id}`}><TestTube className="h-3.5 w-3.5" /> Order labs</button>}
                    {can("hospital.notes.manage") && <button onClick={() => { setEditClinical(a); setEditClinicalForm({ diagnosis: a.diagnosis || "", treatment_plan: a.treatment_plan || "", vitals: a.vitals || "", notes: a.notes || "" }); }}
                      className={ghostBtnCls} data-testid={`ipd-edit-clinical-${a.admission_id}`}><Pencil className="h-3.5 w-3.5" /> Edit clinical details</button>}
                    {canCleanup && <button onClick={() => { setCleanupFor(a); setCleanupReason(""); setCleanupConfirmed(false); setCleanupError(""); }} className="text-xs font-medium text-red-600 hover:text-red-800" data-testid={`cleanup-ipd-${a.admission_id}`}>Remove test record</button>}
                    <button onClick={() => setDischarge(a)} className={ghostBtnCls} data-testid={`discharge-${a.admission_id}`}>Discharge</button>
                  </>
                ) : (
                  <button onClick={() => openCase(a, "discharge")} className={ghostBtnCls} data-testid={`discharge-summary-${a.admission_id}`}>
                    <Printer className="h-3.5 w-3.5" /> Discharge summary
                  </button>
                )}
              </div>
            </Td>
          </tr>
        )} />

            <Modal open={wardModalOpen} onClose={() => { setWardModalOpen(false); setEditWardId(null); setEditBedId(null); }} wide
        title={bedModalTab === "ward" ? "Manage Wards" : "Manage Rooms & Beds"}
        subtitle={bedModalTab === "ward" ? "Add, rename, and edit wards" : "Add, rename, and edit beds"}
        testid="manage-beds-modal">
        <div className="space-y-4">
          <div className="flex gap-1">
            <button onClick={() => setBedModalTab("ward")} className={`px-3 py-2 rounded-md text-sm font-medium ${bedModalTab === "ward" ? "bg-indigo-50 text-indigo-600 border border-indigo-200" : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"}`}>Wards</button>
            <button onClick={() => setBedModalTab("bed")} className={`px-3 py-2 rounded-md text-sm font-medium ${bedModalTab === "bed" ? "bg-indigo-50 text-indigo-600 border border-indigo-200" : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"}`}>Rooms & Beds</button>
          </div>
          {bedModalTab === "ward" ? (
            <>
              <div className="grid gap-3">
                {wards.map((w) => (
                  <div key={w.ward_id} className="rounded-lg border p-4 flex justify-between items-start">
                    <div>
                      <p className="font-ui font-bold text-slate-900">{w.name}</p>
                      <p className="text-xs text-slate-500">{titleize(w.category || w.ward_type || "general")} · {w.is_room ? "Rooms" : "Beds"}: {w.bed_count}</p>
                    </div>
                    <div className="flex gap-2">
                      {editWardId === w.ward_id ? (
                        <div className="flex flex-wrap gap-2">
                          <input className={inputCls} value={editWardForm.name} onChange={(e) => setEditWardForm({ ...editWardForm, name: e.target.value })} />
                          <input className={inputCls} value={editWardForm.bed_count} onChange={(e) => setEditWardForm({ ...editWardForm, bed_count: e.target.value })} />
                          <button onClick={() => {
                            setWardModalBusy(true);
                            api.post(`/hospital/wards`, {
                              name: editWardForm.name,
                              category: editWardForm.category,
                              ward_type: editWardForm.category,
                              bed_count: Number(editWardForm.bed_count) || 0,
                              bed_prefix: editWardForm.bed_prefix,
                              daily_charge: Number(w.daily_charge || 0),
                              facilities: editWardForm.facilities,
                              active: editWardForm.active !== false,
                            }).then(() => {
                              toast.success("Ward created and beds generated.");
                              setEditWardId(null);
                              setWard({ name: "", category: "general", bed_count: "10", bed_prefix: "", daily_charge: "", facilities: "", active: true });
                              load();
                            }).catch((err) => toast.error(apiError(err))).finally(() => setWardModalBusy(false));
                          }} className={primaryBtnCls}>Save ward</button>
                          <button onClick={() => setEditWardId(null)} className={ghostBtnCls}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditWardId(w.ward_id); setEditWardForm({ name: w.name, category: w.category || "general", bed_count: w.bed_count || "", bed_prefix: w.bed_prefix || "", daily_charge: w.daily_charge || "", facilities: w.facilities || "", active: w.active !== false }); }} className="text-slate-400 hover:text-indigo-600" data-testid={`edit-ward-${w.ward_id}`}>
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-xs text-slate-500 mb-3">Add new ward / room category</p>
                <form onSubmit={(e) => { e.preventDefault(); setWardModalBusy(true); api.post("/hospital/wards", {
                  name: ward.name,
                  category: ward.category,
                  ward_type: ward.category,
                  bed_count: Number(ward.bed_count || 0),
                  bed_prefix: ward.bed_prefix,
                  daily_charge: Number(ward.daily_charge || 0),
                  facilities: ward.facilities,
                  active: ward.active,
                }).then(() => {
                  toast.success("Ward created and beds generated.");
                  setWard({ name: "", category: "general", bed_count: "10", bed_prefix: "", daily_charge: "", facilities: "", active: true });
                  load();
                }).catch((err) => toast.error(apiError(err))).finally(() => setWardModalBusy(false)); }} className="grid gap-5 sm:grid-cols-2">
                  <Field label="Ward / category name *"><input required data-testid="ward-name-input" className={inputCls} placeholder="General Ward / Deluxe" value={ward.name} onChange={(e) => setWard({ ...ward, name: e.target.value })} /></Field>
                  <Field label="Category"><select className={inputCls} value={ward.category} data-testid="ward-category-select" onChange={(e) => setWard({ ...ward, category: e.target.value })}>{["general", "semi_private", "private", "deluxe", "super_deluxe", "special", "icu", "maternity", "nicu"].map((t) => <option key={t} value={t}>{titleize(t)}</option>)}</select></Field>
                  <Field label={`Number of ${["deluxe", "super_deluxe", "private", "special"].includes(ward.category) ? "rooms" : "beds"} *`}><input required type="number" min="1" max="200" data-testid="ward-bed-count-input" className={inputCls} value={ward.bed_count} onChange={(e) => setWard({ ...ward, bed_count: e.target.value })} /></Field>
                  <Field label="Bed / room number prefix (optional)"><input data-testid="ward-prefix-input" className={inputCls} placeholder="Auto (e.g. G, D, SD)" value={ward.bed_prefix} onChange={(e) => setWard({ ...ward, bed_prefix: e.target.value })} /></Field>
                  <Field label="Daily charge (₹)"><input type="number" data-testid="ward-charge-input" className={inputCls} value={ward.daily_charge} onChange={(e) => setWard({ ...ward, daily_charge: e.target.value })} /></Field>
                  <Field label="Facilities / description"><input className={inputCls} data-testid="ward-facilities-input" placeholder="AC, attached bath, TV" value={ward.facilities} onChange={(e) => setWard({ ...ward, facilities: e.target.value })} /></Field>
                  <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="ward-submit-btn">Save ward & generate beds</button></div>
                </form>
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-3">
                {wards.flatMap((w) => (w.beds || []).map((b) => (
                  <div key={b.bed_id} className="rounded-lg border p-4 flex justify-between items-start">
                    <div>
                      <p className="font-ui font-bold text-slate-900">{b.bed_number}</p>
                      <p className="text-xs text-slate-500">{w.is_room ? "Room" : "Bed"} · {w.name}</p>
                      <p className={`text-[11px] capitalize font-medium ${b.status === "available" ? "text-emerald-600" : b.status === "occupied" ? "text-red-600" : b.status === "reserved" ? "text-amber-600" : "text-slate-500"}`}>{b.status}</p>
                      {b.status === "reserved" && b.reservation && (
                        <p className="text-[11px] text-amber-700 mt-0.5" data-testid={`bed-reserved-for-${b.bed_id}`}>
                          Reserved for: {b.reservation.patient_name} ({b.reservation.patient_uhid})
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 items-start">
                      {b.status === "available" && can("hospital.beds.manage") && (
                        <button onClick={() => openReserve(b)} className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800" data-testid={`reserve-bed-${b.bed_id}`}>Reserve</button>
                      )}
                      {b.status === "reserved" && can("hospital.beds.manage") && (
                        <button onClick={() => submitCancelReservation(b)} className="text-[11px] font-medium text-red-500 hover:text-red-700" data-testid={`cancel-reserve-${b.bed_id}`}>Cancel</button>
                      )}
                      <button onClick={() => openBedDetails(b)} className="text-slate-400 hover:text-indigo-600" title="Bed details & edit" data-testid={`edit-bed-${b.bed_id}`}>
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )))}
              </div>
              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-xs text-slate-500 mb-3">Add new bed</p>
                <form onSubmit={(e) => { e.preventDefault(); setWardModalBusy(true); api.post("/hospital/beds", bed).then(() => {
                  toast.success("Bed created.");
                  setBed({ ward_id: "", bed_number: "" });
                  load();
                }).catch((err) => toast.error(apiError(err))).finally(() => setWardModalBusy(false)); }} className="grid gap-5 sm:grid-cols-2">
                  <Field label="Ward *"><select required className={inputCls} value={bed.ward_id} data-testid="bed-ward-select" onChange={(e) => setBed({ ...bed, ward_id: e.target.value })}><option value="">Select ward</option>{wards.map((w) => <option key={w.ward_id} value={w.ward_id}>{w.name}</option>)}</select></Field>
                  <Field label="Bed number *"><input required data-testid="bed-number-input" className={inputCls} value={bed.bed_number} onChange={(e) => setBed({ ...bed, bed_number: e.target.value })} /></Field>
                  {formError && <p className="sm:col-span-2 text-sm text-red-600">{formError}</p>}
                  <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="bed-submit-btn">Save bed</button></div>
                </form>
              </div>
            </>
          )}
        </div>
      </Modal>
      <Modal open={admitOpen} onClose={() => { setAdmitOpen(false); setAdmitWardId(""); setAdmit((a) => ({ ...a, package_id: "", package_extra_ids: [] })); }} wide title="Admit patient"
        subtitle="Choose a new or existing patient, then allot a bed and open the case." testid="admit-modal">
        <div className="mb-6"><PatientPicker selected={picked} onSelect={(p) => { setPicked(p); setAdmit({ ...admit, patient_id: p?.patient_id || "" }); }} /></div>
        {picked && (
        <form onSubmit={openAdmitPreview} className="grid gap-5 sm:grid-cols-2">
          <Field label="Consultant doctor *">
            <select required className={inputCls} value={admit.doctor_id} data-testid="admit-doctor-select" onChange={(e) => setAdmit({ ...admit, doctor_id: e.target.value })}>
              <option value="">Select doctor</option>
              {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>)}
            </select>
          </Field>
          <Field label="Ward / room category *">
            <select required className={inputCls} value={admitWardId} data-testid="admit-ward-select" onChange={(e) => chooseAdmitWard(e.target.value)}>
              <option value="">Select ward / category</option>
              {wards.map((w) => {
                const free = freeBeds.filter((b) => b.ward_id === w.ward_id).length;
                return <option key={w.ward_id} value={w.ward_id} disabled={free === 0}>{w.name} · {titleize(w.category || w.ward_type || "general")} ({free} free)</option>;
              })}
            </select>
          </Field>
          <Field label={`${unitWord(admitWard)} *`}>
            <select required className={inputCls} value={admit.bed_id} data-testid="admit-bed-select" onChange={(e) => setAdmit({ ...admit, bed_id: e.target.value })}>
              <option value="">Select available {unitWord(admitWard).toLowerCase()}</option>
              {(admitWardId ? wardFreeBeds : freeBeds).map((b) => <option key={b.bed_id} value={b.bed_id}>{b.ward} · {b.bed_number}</option>)}
            </select>
            {admit.bed_id && admitWard && <p className="mt-1.5 text-xs text-emerald-700" data-testid="admit-selected-bed">Selected: {admitWard.name} · {unitWord(admitWard)} {(wardFreeBeds.find((b) => b.bed_id === admit.bed_id) || {}).bed_number}</p>}
          </Field>
          {packages.length > 0 ? (
            <Field label="Special Package" className="sm:col-span-2">
              <select className={inputCls} value={admit.package_id || ""} data-testid="admit-package-select" onChange={(e) => {
                const pkgId = e.target.value;
                setAdmit({ ...admit, package_id: pkgId, package_extra_ids: [] });
                const selectedPkg = packages.find((p) => p.package_id === pkgId);
                setPackageExtras(selectedPkg?.extras?.filter((ex) => ex.active) || []);
              }}>
                <option value="">No package (standard billing)</option>
                {packages.map((p) => <option key={p.package_id} value={p.package_id}>{p.name} · {titleize(p.category)} · ₹{p.price}</option>)}
              </select>
              {admit.package_id && <p className="mt-1.5 text-xs text-slate-600">
                Package price will be applied to the admission invoice.
              </p>}
            </Field>
          ) : (
            <p className="sm:col-span-2 text-xs text-slate-500">No active special packages available for this hospital.</p>
          )}
          {admit.package_id && packageExtras.length > 0 && (
            <Field label="Package extras (optional)" className="sm:col-span-2">
              <div className="space-y-2">
                {packageExtras.map((ex) => (
                  <label key={ex.service_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" onChange={(e) => setAdmit((a) => ({
                      ...a,
                      package_extra_ids: e.target.checked
                        ? [...(a.package_extra_ids || []), ex.service_id]
                        : (a.package_extra_ids || []).filter((id) => id !== ex.service_id)
                    }))} />
                    <span>{ex.name}</span>
                    <span className="text-slate-400">· {ex.description}</span>
                    <span className="text-emerald-600">₹{ex.charge}</span>
                  </label>
                ))}
              </div>
            </Field>
          )}
          <Field label="Advance payment (₹)"><input type="number" min="0" step="0.01" data-testid="admit-advance-input" className={inputCls} value={admit.advance_payment} onChange={(e) => setAdmit({ ...admit, advance_payment: e.target.value })} /></Field>
          <Field label={`Bill discount (${admit.discount_type === "percent" ? "%" : "₹"})`}>
            <input type="number" min="0" step="0.01"
              max={admit.discount_type === "percent" ? 100 : undefined}
              data-testid="admit-discount-input" className={inputCls} value={admit.discount}
              onChange={(e) => setAdmit({ ...admit, discount: e.target.value })} />
          </Field>
          <Field label="Discount type"><select className={inputCls} data-testid="admit-discount-type" value={admit.discount_type} onChange={(e) => setAdmit({ ...admit, discount_type: e.target.value })}><option value="amount">Amount (₹)</option><option value="percent">Percentage (%)</option></select></Field>
          <Field label="Additional bill tax %"><input type="number" min="0" max="100" step="0.01" data-testid="admit-tax-input" className={inputCls} value={admit.tax_percent} onChange={(e) => setAdmit({ ...admit, tax_percent: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="direct-ipd-consultation-toggle" checked={admit.include_direct_consultation} onChange={(e) => setAdmit({ ...admit, include_direct_consultation: e.target.checked })} /> Add configured doctor consultation for direct IPD only</label>
          <Field label="Vitals on admission"><input className={inputCls} data-testid="admit-vitals-input" placeholder="BP 120/80 · Pulse 78 · Temp 98.6°F" value={admit.vitals} onChange={(e) => setAdmit({ ...admit, vitals: e.target.value })} /></Field>
          <Field label="Provisional diagnosis *" className="sm:col-span-2"><input required className={inputCls} data-testid="admit-diagnosis-input" value={admit.diagnosis} onChange={(e) => setAdmit({ ...admit, diagnosis: e.target.value })} /></Field>
          <Field label="Treatment plan" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="admit-treatment-input" value={admit.treatment_plan} onChange={(e) => setAdmit({ ...admit, treatment_plan: e.target.value })} /></Field>
          <Field label="Admission notes" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={admit.notes} onChange={(e) => setAdmit({ ...admit, notes: e.target.value })} /></Field>
          <div className="sm:col-span-2 space-y-2 rounded-lg border border-slate-200 p-3" data-testid="admit-charges-section">
            <p className="overline text-[#EA580C]">Initial charges (optional)</p>
            <p className="text-xs text-slate-500">Bed charges (1 day) are billed automatically from the ward tariff. Add any treatment / service / item lines below.</p>
            {admitCharges.map((c, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input className={`${inputCls} col-span-4`} placeholder="Treatment / service / item" value={c.description} data-testid={`admit-charge-desc-${idx}`} onChange={(e) => setAdmitCharges(admitCharges.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
                <input type="number" min="1" className={`${inputCls} col-span-2`} placeholder="Qty" value={c.quantity} onChange={(e) => setAdmitCharges(admitCharges.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))} />
                <input type="number" min="0" className={`${inputCls} col-span-2`} placeholder="Rate" value={c.rate} data-testid={`admit-charge-rate-${idx}`} onChange={(e) => setAdmitCharges(admitCharges.map((x, i) => i === idx ? { ...x, rate: e.target.value } : x))} />
                <input type="number" min="0" className={`${inputCls} col-span-2`} placeholder="Disc ₹" value={c.discount} onChange={(e) => setAdmitCharges(admitCharges.map((x, i) => i === idx ? { ...x, discount: e.target.value } : x))} />
                <input type="number" min="0" max="100" className={`${inputCls} col-span-1`} placeholder="GST%" value={c.tax_percent} onChange={(e) => setAdmitCharges(admitCharges.map((x, i) => i === idx ? { ...x, tax_percent: e.target.value } : x))} />
                <button type="button" className="col-span-1 text-slate-400 hover:text-red-600" onClick={() => setAdmitCharges(admitCharges.filter((_, i) => i !== idx))}>×</button>
              </div>
            ))}
            <button type="button" className={ghostBtnCls} data-testid="admit-add-charge" onClick={() => setAdmitCharges([...admitCharges, { description: "", quantity: 1, rate: "", discount: "", tax_percent: "" }])}>+ Add charge line</button>
          </div>
          {formError && <p className="sm:col-span-2 text-sm text-red-600" data-testid="admit-form-error">{formError}</p>}
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="admit-submit-btn" onClick={openAdmitPreview}>Preview bill</button></div>
        </form>
        )}
      </Modal>

      <Modal open={Boolean(noteFor)} onClose={() => setNoteFor(null)} title="Add clinical note"
        subtitle={noteFor ? `${noteFor.patient_name} · Bed ${noteFor.bed_number}` : ""} testid="case-note-modal">
        <form onSubmit={(e) => { e.preventDefault(); save(() => api.post(`/hospital/admissions/${noteFor.admission_id}/notes`, noteForm), () => { setNoteFor(null); toast.success("Clinical note added."); }); }} className="grid gap-5 sm:grid-cols-2">
          <Field label="Note type">
            <select className={inputCls} value={noteForm.note_type} data-testid="note-type-select" onChange={(e) => setNoteForm({ ...noteForm, note_type: e.target.value })}>
              {["progress", "nursing", "diagnostic", "procedure", "medication"].map((t) => <option key={t} value={t}>{titleize(t)}</option>)}
            </select>
          </Field>
          <Field label="Vitals"><input className={inputCls} data-testid="note-vitals-input" value={noteForm.vitals} onChange={(e) => setNoteForm({ ...noteForm, vitals: e.target.value })} /></Field>
          <Field label="Note *" className="sm:col-span-2"><textarea rows={4} required className={inputCls} data-testid="note-text-input" value={noteForm.note} onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="note-submit-btn">Save note</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(service)} onClose={() => setService(null)} title="Add treatment charge" testid="service-modal">
        <form onSubmit={(e) => { e.preventDefault(); save(() => api.post(`/hospital/admissions/${service.admission_id}/services`, { description: sForm.description, amount: Number(sForm.amount || 0) }), () => { setService(null); setSForm({ description: "", amount: "" }); toast.success("Charge added."); }); }} className="grid gap-5 sm:grid-cols-2">
          <Field label="Description *"><input required data-testid="service-desc-input" className={inputCls} value={sForm.description} onChange={(e) => setSForm({ ...sForm, description: e.target.value })} /></Field>
          <Field label="Amount (₹) *"><input required type="number" data-testid="service-amount-input" className={inputCls} value={sForm.amount} onChange={(e) => setSForm({ ...sForm, amount: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="service-submit-btn">Add charge</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(chargesFor)} onClose={() => { setChargesFor(null); setReverseChargeTarget(null); setReverseReason(""); }} wide
        title="Charges & non-destructive reversal"
        subtitle="Reversing a charge never deletes it — the original entry stays in history and is excluded from billing." testid="charges-modal">
        {reverseChargeTarget ? (
          <form onSubmit={submitReverseCharge} className="space-y-4" data-testid="reverse-charge-form">
            <p className="text-sm text-slate-600">Reversing <strong>{reverseChargeTarget.description}</strong> ({inr(reverseChargeTarget.amount)}). A reason is mandatory.</p>
            <Field label="Reversal reason *"><textarea required rows={3} className={inputCls} data-testid="reverse-charge-reason" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} /></Field>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setReverseChargeTarget(null); setReverseReason(""); }} className={ghostBtnCls}>Cancel</button>
              <button className={primaryBtnCls} data-testid="reverse-charge-confirm">Confirm reversal</button>
            </div>
          </form>
        ) : (
          <div className="space-y-2" data-testid="charges-list">
            {(chargesFor?.services || []).length === 0 && <EmptyState title="No charges yet" message="Add a treatment charge first." />}
            {(chargesFor?.services || []).map((sv) => (
              <div key={sv.service_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`charge-row-${sv.service_id}`}>
                <div>
                  <span className={sv.status === "reversed" ? "line-through text-slate-400" : "text-slate-800"}>{sv.description}</span>
                  {sv.status === "reversed" && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-600">Reversed · {sv.reversal?.reason}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className={sv.status === "reversed" ? "text-slate-400" : "text-slate-800"}>{inr(sv.amount)}</span>
                  {sv.status !== "reversed" && can("hospital.billing.manage") && (
                    <button onClick={() => { setReverseChargeTarget(sv); setReverseReason(""); }} className="text-xs text-red-600 hover:underline" data-testid={`reverse-charge-${sv.service_id}`}>Reverse</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={Boolean(nurseFor)} onClose={() => setNurseFor(null)} wide
        title="Nurse / care staff assignment"
        subtitle="Reassigning keeps the previous assignment as a closed history record — never overwritten." testid="nurse-modal">
        <form onSubmit={submitNurse} className="grid gap-4 sm:grid-cols-3" data-testid="nurse-assign-form">
          <Field label="Staff member *">
            <select required className={inputCls} data-testid="nurse-staff-select" value={nurseForm.staff_user_id}
              onChange={(e) => setNurseForm({ ...nurseForm, staff_user_id: e.target.value })}>
              <option value="">Select staff</option>
              {staff.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {titleize(u.role || "")}</option>)}
            </select>
          </Field>
          <Field label="Role / shift label"><input className={inputCls} data-testid="nurse-role-input" value={nurseForm.role_label} onChange={(e) => setNurseForm({ ...nurseForm, role_label: e.target.value })} /></Field>
          <Field label="Reason"><input className={inputCls} data-testid="nurse-reason-input" value={nurseForm.reason} onChange={(e) => setNurseForm({ ...nurseForm, reason: e.target.value })} /></Field>
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="nurse-assign-btn">Assign staff</button></div>
        </form>
        <div className="mt-5 space-y-2" data-testid="nurse-history">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assignment history</p>
          {nurseHistory.length === 0 && <p className="text-sm text-slate-400">No assignments yet.</p>}
          {nurseHistory.map((h) => (
            <div key={h.assignment_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span className="text-slate-800">{h.staff_name} <span className="text-slate-400">· {h.role_label}</span></span>
              <span className="text-xs text-slate-500">{dmyTime(h.start_at)} → {h.end_at ? dmyTime(h.end_at) : <span className="text-emerald-600 font-medium">Active</span>}</span>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={Boolean(discharge)} onClose={() => setDischarge(null)} wide title="Discharge patient"
        subtitle="The bed is released, the final IPD invoice is generated and the discharge summary becomes printable." testid="discharge-modal">
        <form onSubmit={(e) => { e.preventDefault(); const a = discharge; save(() => api.post(`/hospital/admissions/${a.admission_id}/discharge`, dForm), () => { setDischarge(null); toast.success("Patient discharged and final invoice created."); openCase(a, "discharge"); }); }} className="grid gap-5 sm:grid-cols-2">
          <Field label="Final / discharge diagnosis *"><input required className={inputCls} data-testid="discharge-diagnosis-input" value={dForm.discharge_diagnosis} onChange={(e) => setDForm({ ...dForm, discharge_diagnosis: e.target.value })} /></Field>
          <Field label="Condition at discharge"><input className={inputCls} data-testid="discharge-condition-input" value={dForm.condition_at_discharge} onChange={(e) => setDForm({ ...dForm, condition_at_discharge: e.target.value })} /></Field>
          <Field label="Hospital course" className="sm:col-span-2"><textarea rows={3} className={inputCls} data-testid="discharge-course-input" value={dForm.hospital_course} onChange={(e) => setDForm({ ...dForm, hospital_course: e.target.value })} /></Field>
          <Field label="Treatment / procedures performed" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="discharge-procedures-input" value={dForm.procedures} onChange={(e) => setDForm({ ...dForm, procedures: e.target.value })} /></Field>
          <Field label="Discharge medicines" className="sm:col-span-2"><textarea rows={3} className={inputCls} data-testid="discharge-medicines-input" placeholder={"Tab Paracetamol 500 mg — 1-0-1 × 5 days"} value={dForm.discharge_medicines} onChange={(e) => setDForm({ ...dForm, discharge_medicines: e.target.value })} /></Field>
          <Field label="Follow-up date"><input type="date" className={inputCls} data-testid="discharge-followup-date" value={dForm.follow_up_date} onChange={(e) => setDForm({ ...dForm, follow_up_date: e.target.value })} /></Field>
          <Field label="Follow-up instructions"><input className={inputCls} data-testid="discharge-instructions-input" value={dForm.follow_up_instructions} onChange={(e) => setDForm({ ...dForm, follow_up_instructions: e.target.value })} /></Field>
          <Field label="Summary" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={dForm.summary} onChange={(e) => setDForm({ ...dForm, summary: e.target.value })} /></Field>
          {formError && <p className="sm:col-span-2 text-sm text-red-600">{formError}</p>}
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="discharge-submit-btn">Confirm discharge</button></div>
        </form>
      </Modal>

      <Modal
        open={Boolean(invoiceFor)}
        onClose={() => setInvoiceFor(null)}
        wide
        title="IPD invoice & payment"
        subtitle={invoiceFor?.admission ? `${invoiceFor.admission.patient_name}${invoiceFor.admission.admission_no ? ` · ${invoiceFor.admission.admission_no}` : ""}` : ""}
        testid="ipd-invoice-modal"
      >
        {(() => {
          try {
            const st = invoiceFor.settlement || {};
            const inv = invoiceFor.invoice;
            return (
          <div className="space-y-6">
            <div className="grid gap-3 rounded-lg border border-slate-200 p-4 text-sm sm:grid-cols-4" data-testid="ipd-settlement-summary">
              <div><p className="text-xs text-slate-500">Active charges</p><p className="font-semibold" data-testid="ipd-charges-total">{inr(st.charges_total || 0)}</p></div>
              <div><p className="text-xs text-slate-500">Advance / deposit</p><p className="font-semibold">{inr(st.advance_total || 0)}</p></div>
              <div><p className="text-xs text-slate-500">Advance applied</p><p className="font-semibold text-sky-700" data-testid="ipd-advance-applied">{inr(st.advance_applied || 0)}</p></div>
              <div><p className="text-xs text-slate-500">Refundable advance</p><p className="font-semibold text-indigo-700" data-testid="ipd-advance-refundable">{inr(st.advance_refundable || 0)}</p></div>
              <div><p className="text-xs text-slate-500">Collected</p><p className="font-semibold text-emerald-700">{inr(st.collected || 0)}</p></div>
              {st.advance_refunded > 0 && <div><p className="text-xs text-slate-500">Refunded</p><p className="font-semibold text-rose-700">{inr(st.advance_refunded)}</p></div>}
              <div><p className="text-xs text-slate-500">Total paid</p><p className="font-semibold text-emerald-700">{inr(st.paid || 0)}</p></div>
              <div><p className="text-xs text-slate-500">Outstanding</p><p className="font-semibold text-amber-700" data-testid="ipd-invoice-due">{inr(st.outstanding || 0)}</p></div>
            </div>
            {inv && <p className="-mt-3 text-xs text-slate-500">Invoice <span className="font-semibold text-slate-700" data-testid="ipd-invoice-no">{inv.invoice_no}</span> · Admission {invoiceFor.admission?.admission_no || "—"}</p>}

            {invoiceReverseTarget ? (
              <form onSubmit={submitInvoiceReverse} className="rounded-lg border border-red-200 bg-red-50/40 p-4 space-y-3"
                data-testid="ipd-invoice-reverse-form">
                <p className="text-sm text-slate-700">Reversing <strong>{invoiceReverseTarget.description}</strong> ({inr(invoiceReverseTarget.amount)}). A reason is mandatory.</p>
                <Field label="Reversal reason *"><textarea required rows={3} className={inputCls}
                  data-testid="ipd-invoice-reverse-reason" value={invoiceReverseReason}
                  onChange={(e) => setInvoiceReverseReason(e.target.value)} /></Field>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setInvoiceReverseTarget(null); setInvoiceReverseReason(""); }} className={ghostBtnCls}>Cancel</button>
                  <button className={primaryBtnCls} data-testid="ipd-invoice-reverse-confirm">Confirm reversal</button>
                </div>
              </form>
            ) : (
              <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="ipd-invoice-charges-section">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="overline text-[#EA580C]">Charges</p>
                  <p className="text-xs text-slate-500">Reversal keeps the original entry in history and excludes it from billing.</p>
                </div>
                {invoiceCharges.length === 0 ? (
                  <p className="text-sm text-slate-400">No treatment charges recorded yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="ipd-invoice-charges-list">
                    {invoiceCharges.map((sv) => (
                      <div key={sv.service_id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm"
                        data-testid={`ipd-invoice-charge-row-${sv.service_id}`}>
                        <div>
                          <span className={sv.status === "reversed" ? "line-through text-slate-400" : "text-slate-800"}>{sv.description}</span>
                          {sv.status === "reversed" && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-600">Reversed · {sv.reversal?.reason}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={sv.status === "reversed" ? "text-slate-400" : "text-slate-800"}>{inr(sv.amount)}</span>
                          {sv.status !== "reversed" && can("hospital.billing.manage") && (
                            <button type="button" onClick={() => { setInvoiceReverseTarget(sv); setInvoiceReverseReason(""); }}
                              className="text-xs text-red-600 hover:underline"
                              data-testid={`ipd-invoice-reverse-${sv.service_id}`}>Reverse</button>
                          )}
                        </div>
                      </div>
                    ))}
</div>
            )}>
            )}
                <form onSubmit={addInvoiceCharge} className="grid gap-3 sm:grid-cols-3 pt-2 border-t border-slate-100">
                  <input className={`${inputCls} sm:col-span-2`} placeholder="Charge description (e.g. IV fluids)"
                    data-testid="ipd-invoice-add-charge-desc" value={invoiceServiceForm.description}
                    onChange={(e) => setInvoiceServiceForm({ ...invoiceServiceForm, description: e.target.value })} />
                  <input type="number" min="0" step="0.01" className={inputCls} placeholder="Amount (₹)"
                    data-testid="ipd-invoice-add-charge-amount" value={invoiceServiceForm.amount}
                    onChange={(e) => setInvoiceServiceForm({ ...invoiceServiceForm, amount: e.target.value })} />
                  <div className="sm:col-span-3">
                    <button className={ghostBtnCls} data-testid="ipd-invoice-add-charge-submit">
                      <Plus className="h-3.5 w-3.5" /> Add charge &amp; refresh
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!invoiceReverseTarget && (st.advance_refundable || 0) > 0 && can("hospital.payments.manage") && (
              showRefund ? (
                <form onSubmit={submitRefund} className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="ipd-refund-form">
                  <p className="text-sm text-slate-700">Refund the unused advance. Refundable balance: <strong>{inr(st.advance_refundable)}</strong>. The original deposit stays in history.</p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Refund amount (₹) *"><input required type="number" min="0.01" step="0.01" max={st.advance_refundable} className={inputCls} data-testid="ipd-refund-amount" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} /></Field>
                    <Field label="Mode"><select className={inputCls} data-testid="ipd-refund-mode" value={refundForm.mode} onChange={(e) => setRefundForm({ ...refundForm, mode: e.target.value })}>{["cash", "upi", "card", "bank", "cheque", "insurance"].map((m) => <option key={m} value={m}>{titleize(m)}</option>)}</select></Field>
                    <Field label="Reference"><input className={inputCls} data-testid="ipd-refund-reference" value={refundForm.reference_no} onChange={(e) => setRefundForm({ ...refundForm, reference_no: e.target.value })} /></Field>
                  </div>
                  <Field label="Reason *"><textarea required rows={2} className={inputCls} data-testid="ipd-refund-reason" value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} /></Field>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowRefund(false)} className={ghostBtnCls}>Cancel</button>
                    <button className={primaryBtnCls} data-testid="ipd-refund-submit">Confirm refund</button>
                  </div>
                </form>
              ) : (
                <button type="button" className={ghostBtnCls} data-testid="ipd-refund-open" onClick={() => { setShowRefund(true); setRefundForm({ amount: String(st.advance_refundable || ""), mode: "cash", reference_no: "", reason: "" }); }}>Refund remaining advance ({inr(st.advance_refundable)})</button>
              )
            )}

            {!invoiceReverseTarget && !showRefund && inv && inv.outstanding > 0 && can("hospital.payments.manage") && (
              <div className="rounded-lg border border-slate-200 p-4 space-y-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <input type="checkbox" checked={splitMode}
                    onChange={(e) => { setSplitMode(e.target.checked); if (!e.target.checked) setSplitPayments([{ amount: "", mode: "cash", reference_no: "" }]); }} />
                  <label>Split payment across multiple methods</label>
                </div>
                {splitMode ? (
                  <form onSubmit={collectSplitInvoicePayment} className="space-y-3" data-testid="ipd-invoice-split-pay-form">
                    <div className="space-y-2" data-testid="ipd-invoice-split-rows">
                      {splitPayments.map((payment, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded p-2">
                          <div className="col-span-4">
                            <Field label={`Amount (₹) #${idx + 1}`}>
                              <input required type="number" min="0.01" step="0.01" max={inv.outstanding} className={inputCls}
                                value={payment.amount}
                                onChange={(event) => setSplitPayments(splitPayments.map((p, i) => i === idx ? { ...p, amount: event.target.value } : p))} />
                            </Field>
                          </div>
                          <div className="col-span-4">
                            <Field label="Mode">
                              <select className={inputCls} value={payment.mode}
                                onChange={(event) => setSplitPayments(splitPayments.map((p, i) => i === idx ? { ...p, mode: event.target.value } : p))}>
                                {["cash", "upi", "card", "bank", "insurance", "cheque", "online", "other"].map((mode) => (
                                  <option key={mode} value={mode}>{titleize(mode)}</option>
                                ))}
                              </select>
                            </Field>
                          </div>
                          <div className="col-span-3">
                            <Field label="Reference">
                              <input className={inputCls} value={payment.reference_no}
                                onChange={(event) => setSplitPayments(splitPayments.map((p, i) => i === idx ? { ...p, reference_no: event.target.value } : p))} />
                            </Field>
                          </div>
                          <div className="col-span-1 flex items-end">
                            <button type="button" onClick={() => setSplitPayments(splitPayments.filter((_, i) => i !== idx))}
                              className={ghostBtnCls} data-testid={`ipd-invoice-split-remove-${idx}`}>×</button>
                          </div>
                        </div>
                      ))}
                      <div className="col-span-12">
                        <button type="button" onClick={() => setSplitPayments([...splitPayments, { amount: "", mode: "cash", reference_no: "" }])}
                          className={ghostBtnCls} data-testid="ipd-invoice-split-add">+ Add payment method</button>
                      </div>
                      <div className="col-span-12 text-xs text-slate-500" data-testid="ipd-invoice-split-total">
                        Split total: <strong>{inr(round2(splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0)))}</strong>
                        of outstanding {inr(Number(inv.outstanding || 0))}
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button type="button" onClick={() => setSplitMode(false)} className={ghostBtnCls}>Cancel split</button>
                      <button disabled={paymentSubmitting} className={primaryBtnCls} data-testid="ipd-invoice-split-submit">
                        {paymentSubmitting ? "Recording…" : "Record split payment"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={collectInvoicePayment} className="grid gap-4 sm:grid-cols-3 rounded-lg border border-slate-200 p-4">
                    <p className="sm:col-span-3 overline text-[#EA580C]">Collect additional payment</p>
                    <Field label="Payment amount (₹) *">
                      <input required min="0.01" max={inv.outstanding} step="0.01" type="number" className={inputCls}
                        data-testid="ipd-invoice-payment-amount" value={invoicePayment.amount}
                        onChange={(event) => setInvoicePayment({ ...invoicePayment, amount: event.target.value })} />
                    </Field>
                    <Field label="Mode">
                      <select className={inputCls} data-testid="ipd-invoice-payment-mode" value={invoicePayment.mode}
                        onChange={(event) => setInvoicePayment({ ...invoicePayment, mode: event.target.value })}>
                      {["cash", "upi", "card", "bank", "insurance", "cheque", "online", "other"].map((mode) => (
                        <option key={mode} value={mode}>{titleize(mode)}</option>
                      ))}
                      </select>
                    </Field>
                    <Field label="Reference">
                      <input className={inputCls} data-testid="ipd-invoice-payment-reference" value={invoicePayment.reference_no}
                        onChange={(event) => setInvoicePayment({ ...invoicePayment, reference_no: event.target.value })} />
                    </Field>
                    <div className="sm:col-span-3 flex flex-wrap gap-3">
                      <button type="button" className={ghostBtnCls} data-testid="ipd-invoice-pay-full"
                        onClick={() => setInvoicePayment({ ...invoicePayment, amount: String(inv.outstanding) })}>Pay full balance</button>
                      <button disabled={paymentSubmitting} className={primaryBtnCls} data-testid="ipd-invoice-payment-submit">
                        {paymentSubmitting ? "Recording…" : "Record payment"}
                      </button>
                      <button type="button" className={ghostBtnCls} data-testid="ipd-invoice-preview-btn"
                        onClick={previewInvoiceBill}>
                        <Printer className="h-4 w-4" /> Preview bill
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
            {!invoiceReverseTarget && !showRefund && inv && inv.outstanding <= 0 && (
              <button type="button" className={primaryBtnCls} data-testid="ipd-paid-invoice-print-btn"
                onClick={previewInvoiceBill}>
                <Printer className="h-4 w-4" /> Preview &amp; print settlement
              </button>
            )}
            {!inv && <p className="text-sm text-slate-400" data-testid="ipd-no-charges-note">No billable charges yet. The advance / deposit is held on the admission and can be refunded above.</p>}
          </div>
            );
          } catch (e) {
            return <p className="text-sm text-red-600">Could not load this invoice view. Please close and reopen.</p>;
          }
        })()}
      </Modal>

      <Modal open={Boolean(bedCorrection)} onClose={() => { setBedCorrection(null); setCorrectionReason(""); setCorrectionStatus("available"); }}
        title="Correct bed status" subtitle="Release a bed that still shows occupied after the patient has been discharged."
        testid="bed-correct-modal">
        <form onSubmit={submitBedCorrection} className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <p><strong>Bed:</strong> {bedCorrection?.bed_number} · <strong>Ward:</strong> {bedCorrection?.ward_name || "—"}</p>
            <p><strong>Current status:</strong> <span className="text-red-600 font-medium">{bedCorrection?.status}</span></p>
            {bedCorrection?.admission_id && <p><strong>Linked admission:</strong> {bedCorrection.admission_id}</p>}
          </div>
          <Field label="Correct status to *">
            <select required className={inputCls} value={correctionStatus} data-testid="bed-correction-status" onChange={(e) => setCorrectionStatus(e.target.value)}>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="reserved">Reserved</option>
              <option value="maintenance">Under maintenance</option>
            </select>
          </Field>
          <Field label="Correction reason *"><textarea required rows={3} className={inputCls}
            data-testid="bed-correction-reason" value={correctionReason}
            onChange={(e) => setCorrectionReason(e.target.value)} /></Field>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => { setBedCorrection(null); setCorrectionReason(""); setCorrectionStatus("available"); }} className={ghostBtnCls}>Cancel</button>
            <button disabled={correctionBusy} className={primaryBtnCls} data-testid="bed-correction-submit">
              {correctionBusy ? "Saving…" : "Save bed status"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(bedDetails)} onClose={() => setBedDetails(null)} wide
        title="Bed details" subtitle={bedDetails?.bed ? `${bedDetails.bed.bed_number} · ${(bedDetails.ward || {}).name || "—"}` : ""}
        testid="bed-details-modal">
        {bedDetailsBusy ? <Loading /> : bedDetails && (
          <div className="space-y-4 text-sm" data-testid="bed-details-content">
            {(bedDetails.warnings || []).length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-1" data-testid="bed-details-warnings">
                {bedDetails.warnings.map((w, i) => <p key={i} className="text-amber-800 text-xs">⚠ {w}</p>)}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-slate-500">Bed / room</p><p className="font-medium">{bedDetails.bed?.bed_number}</p></div>
              <div><p className="text-xs text-slate-500">Ward</p><p className="font-medium">{(bedDetails.ward || {}).name || "—"}</p></div>
              <div><p className="text-xs text-slate-500">Status</p>
                <p className={`font-medium capitalize ${bedDetails.bed?.status === "available" ? "text-emerald-600" : bedDetails.bed?.status === "occupied" ? "text-red-600" : bedDetails.bed?.status === "reserved" ? "text-amber-600" : "text-slate-600"}`}>{bedDetails.bed?.status}</p></div>
              <div><p className="text-xs text-slate-500">Type</p><p className="font-medium">{(bedDetails.ward || {}).is_room ? "Room" : "Bed"}</p></div>
            </div>
            {bedDetails.bed?.status === "reserved" && bedDetails.bed?.reservation && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="bed-details-reservation">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Reservation</p>
                <p><span className="text-slate-500">Patient:</span> {bedDetails.bed.reservation.patient_name} ({bedDetails.bed.reservation.patient_uhid})</p>
                {bedDetails.bed.reservation.expected_admission_date && <p><span className="text-slate-500">Expected admission:</span> {bedDetails.bed.reservation.expected_admission_date}</p>}
                {bedDetails.bed.reservation.notes && <p><span className="text-slate-500">Notes:</span> {bedDetails.bed.reservation.notes}</p>}
                <p className="text-xs text-slate-400 mt-1">Reserved by {bedDetails.bed.reservation.reserved_by_name} on {bedDetails.bed.reservation.reserved_at ? new Date(bedDetails.bed.reservation.reserved_at).toLocaleString() : "—"}</p>
              </div>
            )}
            {bedDetails.occupant ? (
              <div className="rounded-lg border border-slate-200 p-3" data-testid="bed-details-occupant">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Current occupant {bedDetails.source === "maternity" ? "(Maternity)" : "(IPD)"}
                </p>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div><span className="text-slate-500">Patient:</span> <span className="font-medium">{bedDetails.occupant.patient_name || "—"}</span></div>
                  <div><span className="text-slate-500">UHID:</span> {bedDetails.occupant.patient_uhid || "—"}</div>
                  <div><span className="text-slate-500">Record:</span> {bedDetails.occupant.admission_no || bedDetails.occupant.record_no || bedDetails.occupant.admission_id || bedDetails.occupant.maternity_id}</div>
                  <div><span className="text-slate-500">Status:</span> <span className="capitalize">{bedDetails.occupant.status}</span></div>
                  {bedDetails.occupant.admitted_at && <div><span className="text-slate-500">Admitted:</span> {new Date(bedDetails.occupant.admitted_at).toLocaleDateString()}</div>}
                  {bedDetails.occupant.discharged_at && <div><span className="text-slate-500">Discharged:</span> {new Date(bedDetails.occupant.discharged_at).toLocaleDateString()}</div>}
                  {bedDetails.occupant.diagnosis && <div className="col-span-2"><span className="text-slate-500">Diagnosis:</span> {bedDetails.occupant.diagnosis}</div>}
                </div>
                {bedDetails.occupant.invoice && (
                  <div className="mt-2 pt-2 border-t border-slate-100" data-testid="bed-details-invoice">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">Invoice</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-slate-500">Invoice no:</span> {bedDetails.occupant.invoice.invoice_no}</div>
                      <div><span className="text-slate-500">Status:</span> <span className="capitalize">{bedDetails.occupant.invoice.status}</span></div>
                      <div><span className="text-slate-500">Total:</span> ₹{bedDetails.occupant.invoice.total}</div>
                      <div><span className="text-slate-500">Outstanding:</span> ₹{bedDetails.occupant.invoice.outstanding}</div>
                    </div>
                  </div>
                )}
              </div>
            ) : bedDetails.bed?.status !== "reserved" ? (
              <p className="text-slate-400 italic" data-testid="bed-details-no-occupant">No active occupant</p>
            ) : null}
            <div className="flex justify-end">
              <button onClick={() => setBedDetails(null)} className={ghostBtnCls}>Close</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(reserveFor)} onClose={() => setReserveFor(null)} wide
        title="Reserve bed" subtitle={reserveFor ? `${reserveFor.bed_number}` : ""}
        testid="bed-reserve-modal">
        <form onSubmit={submitReserve} className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <p><strong>Bed:</strong> {reserveFor?.bed_number} · <strong>Status:</strong> {reserveFor?.status}</p>
          </div>
          <PatientPicker selected={reservePatient} onSelect={(p) => { setReservePatient(p); setReserveForm((f) => ({ ...f, patient_id: p?.patient_id || "" })); }} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Expected admission date"><input type="date" className={inputCls} data-testid="reserve-expected-date" value={reserveForm.expected_admission_date} onChange={(e) => setReserveForm({ ...reserveForm, expected_admission_date: e.target.value })} /></Field>
            <Field label="Notes / reason"><input className={inputCls} data-testid="reserve-notes" placeholder="Optional" value={reserveForm.notes} onChange={(e) => setReserveForm({ ...reserveForm, notes: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setReserveFor(null)} className={ghostBtnCls}>Cancel</button>
            <button type="submit" disabled={reserveBusy || !reservePatient?.patient_id} className={primaryBtnCls} data-testid="reserve-submit">
              {reserveBusy ? "Saving…" : "Reserve bed"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(editClinical)} onClose={() => setEditClinical(null)} wide
        title="Edit clinical details"
        subtitle="Correct the provisional diagnosis, treatment plan, vitals or notes after admission."
        testid="edit-clinical-modal">
        <form onSubmit={(e) => {
          e.preventDefault();
          if (editClinicalBusy) return;
          setEditClinicalBusy(true);
          api.patch(`/hospital/admissions/${editClinical.admission_id}`, editClinicalForm)
            .then(() => { toast.success("Clinical details updated."); setEditClinical(null); load(); })
            .catch((err) => toast.error(apiError(err)))
            .finally(() => setEditClinicalBusy(false));
        }} className="grid gap-5 sm:grid-cols-2">
          <Field label="Provisional diagnosis *" className="sm:col-span-2"><input required className={inputCls} data-testid="edit-clinical-diagnosis" value={editClinicalForm.diagnosis} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, diagnosis: e.target.value })} /></Field>
          <Field label="Treatment plan" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="edit-clinical-treatment" value={editClinicalForm.treatment_plan} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, treatment_plan: e.target.value })} /></Field>
          <Field label="Vitals on admission"><input className={inputCls} data-testid="edit-clinical-vitals" value={editClinicalForm.vitals} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, vitals: e.target.value })} /></Field>
          <Field label="Admission notes" className="sm:col-span-2"><textarea rows={2} className={inputCls} data-testid="edit-clinical-notes" value={editClinicalForm.notes} onChange={(e) => setEditClinicalForm({ ...editClinicalForm, notes: e.target.value })} /></Field>
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={() => setEditClinical(null)} className={ghostBtnCls}>Cancel</button>
            <button disabled={editClinicalBusy} className={primaryBtnCls} data-testid="edit-clinical-submit">
              {editClinicalBusy ? "Saving…" : "Save clinical details"}
            </button>
          </div>
        </form>
      </Modal>

      <PrintPreview open={Boolean(invoiceReceipt)} paperSize="A4" title="IPD invoice receipt"
        subtitle={invoiceReceipt?.patient?.name} testid="ipd-invoice-receipt-preview"
        onClose={() => setInvoiceReceipt(null)}>
        <HospitalInvoiceDoc invoice={invoiceReceipt?.invoice} patient={invoiceReceipt?.patient} business={invoiceReceipt?.business} />
      </PrintPreview>

      <Modal open={Boolean(rxFor)} onClose={() => setRxFor(null)} wide title="Create prescription"
        subtitle={rxFor ? `${rxFor.patient_name} · IPD` : ""} testid="ipd-prescription-modal">
        <form onSubmit={saveRx} className="space-y-6">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Doctor *">
              <select className={inputCls} value={rxForm.doctor_id} data-testid="rx-doctor-select"
                onChange={(e) => setRxForm({ ...rxForm, doctor_id: e.target.value })}>
                <option value="">Select doctor</option>
                {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>)}
              </select>
            </Field>
            <Field label="Diagnosis" className="sm:col-span-2"><input className={inputCls} data-testid="rx-diagnosis-input" value={rxForm.diagnosis} onChange={(e) => setRxForm({ ...rxForm, diagnosis: e.target.value })} /></Field>
          </div>
          <div className="space-y-3">
            <p className="overline text-[#EA580C]">Medicines</p>
            {rxForm.medicines.map((m, idx) => (
              <div key={idx} className="grid gap-3 sm:grid-cols-6" data-testid={`rx-line-${idx}`}>
                {[["name", "Medicine (e.g. Paracetamol)"], ["strength", "Strength (e.g. 500 mg)"], ["dosage", "Dosage (e.g. 1 tab)"],
                  ["frequency", "Frequency (e.g. 1-0-1)"], ["duration", "Duration (e.g. 5 days)"], ["instructions", "Instructions (e.g. after food)"]].map(([k, ph]) => (
                  <input key={k} placeholder={ph} className={inputCls} data-testid={`rx-${k}-${idx}`} value={m[k]}
                    onChange={(e) => setRxForm({ ...rxForm, medicines: rxForm.medicines.map((x, i) => i === idx ? { ...x, [k]: e.target.value } : x) })} />
                ))}
              </div>
            ))}
            <button type="button" onClick={() => setRxForm({ ...rxForm, medicines: [...rxForm.medicines, { ...EMPTY_RX_LINE }] })}
              className={ghostBtnCls} data-testid="rx-add-medicine-btn">Add medicine</button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Advice / instructions"><textarea rows={2} className={inputCls} data-testid="rx-advice-input" value={rxForm.advice} onChange={(e) => setRxForm({ ...rxForm, advice: e.target.value })} /></Field>
            <Field label="Follow-up date"><input type="date" className={inputCls} value={rxForm.follow_up_date} onChange={(e) => setRxForm({ ...rxForm, follow_up_date: e.target.value })} /></Field>
          </div>
          <button className={primaryBtnCls} data-testid="rx-submit-btn">Create & preview prescription</button>
        </form>
      </Modal>

      <PrintPreview open={Boolean(caseData)} onClose={() => setCaseData(null)}
        paperSize={previewKind === "discharge"
          ? dischargeTpl?.paper_size || "A4"
          : caseTpl?.paper_size || "A4"}
        template={previewKind === "discharge" ? dischargeTpl : caseTpl}
        title={previewKind === "discharge" ? "Discharge summary" : "IPD case sheet"}
        subtitle={caseData?.admission?.patient_name} testid="case-print-preview">
        {previewKind === "discharge"
          ? <DischargeSummaryDoc data={caseData} template={dischargeTpl} />
          : <CaseSheetDoc data={caseData} template={caseTpl} />}
      </PrintPreview>

      <PrintPreview open={Boolean(rxPreview)} onClose={() => setRxPreview(null)} paperSize={rxTpl?.paper_size || "A4"}
        template={rxTpl} title="Prescription" subtitle={rxPreview?.rx?.patient_name} prescriptionPreview
        testid="ipd-rx-print-preview">
        <PrescriptionDoc rx={rxPreview?.rx} business={rxPreview?.business} template={rxTpl} />
      </PrintPreview>

      <Modal open={admitPreview} onClose={() => setAdmitPreview(false)} title="Preview admission bill"
        subtitle="Review the initial bill before saving. The bed is claimed only when you Save & Print."
        testid="admit-bill-preview-modal" wide>
        <AdmitBillPreview
          lines={admitPreviewLines}
          totals={admitPreviewTotals}
          patient={picked}
          doctor={doctors.find((d) => d.doctor_id === admit.doctor_id)}
          ward={admitWard}
          bed={(wardFreeBeds.find((x) => x.bed_id === admit.bed_id) || {})}
          pkg={packages.find((p) => p.package_id === admit.package_id)}
          packageExtraIds={admit.package_extra_ids || []}
          formError={formError}
          admitSaving={admitSaving}
          onCancel={() => setAdmitPreview(false)}
          onSave={confirmAdmit}
        />
      </Modal>

      <PrintPreview open={Boolean(admitReceipt)} paperSize="A4" title="IPD admission receipt"
        subtitle={admitReceipt?.patient?.name} testid="admit-receipt-preview"
        onClose={() => setAdmitReceipt(null)}>
        <HospitalInvoiceDoc invoice={admitReceipt?.invoice} patient={admitReceipt?.patient} business={admitReceipt?.business} />
      </PrintPreview>

      <Modal open={Boolean(labFor)} onClose={() => setLabFor(null)} title="Order lab tests"
        subtitle={labFor ? `${labFor.patient_name} · Dr. ${labFor.doctor_name}` : ""} testid="ipd-lab-modal">
        <form onSubmit={submitLabs} className="space-y-4">
          <p className="text-xs text-slate-500">Creates a lab order in <strong>pending billing</strong>. Front desk creates the invoice and collects payment before sample collection.</p>
          <div className="border border-slate-200 rounded-lg divide-y max-h-64 overflow-auto">
            {labTests.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">No active tests. Add tests in the Lab catalogue first.</p>}
            {labTests.map((t) => (
              <label key={t.test_id} className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`ipd-lab-option-${t.test_id}`}>
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={labSel.includes(t.test_id)}
                    onChange={(e) => setLabSel(e.target.checked ? [...labSel, t.test_id] : labSel.filter((x) => x !== t.test_id))} />
                  {t.name} <span className="text-slate-400">· {t.category || "—"}</span>
                </span>
                <span className="text-slate-600">₹{t.price}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end"><button className={primaryBtnCls} data-testid="ipd-lab-submit-btn">Create lab order</button></div>
        </form>
      </Modal>

      <p className="mt-6 text-xs text-slate-500">
        Discharging a patient immediately frees their bed on the bed map, and bed charges are billed
        automatically from the ward's daily tariff.
      </p>
    </div>
  );
}
